import { ACTION, isWorkIqToolName, lineIsAction } from "~vue/chatActions";
import { i18n } from "~vue/i18n";
import type { ChatMessage, ChatMessageLine } from "~vue/types/chat";
import { computed, type ComputedRef } from "vue";

/**
 * Tool-call action params. TOOL_CALL lines fall into the untyped generic action arm of
 * ChatMessageLine, so read them through this instead of indexing `Record<string, unknown>`.
 * Source: `ToolCallActionParams` handling in vpoc/apps/nadia_events/.
 */
type ToolCallParams = { tool_call_id?: string; tool_name?: string };

export const toolCallParams = (line: ChatMessageLine): ToolCallParams => (lineIsAction(line) && line.action_name === ACTION.TOOL_CALL ? line.action_params : {});

/** Params of the memory-lookup LOADING action ("Checking my notes...") — see MemoryResults.start_notify. */
type MemoryLoadingParams = { loaded?: boolean; content?: string };

export const memoryLoadingParams = (line: ChatMessageLine): MemoryLoadingParams => (lineIsAction(line) && line.action_name === ACTION.LOADING ? line.action_params : {});

/**
 * A tool-result line carries the id of the call it answers under whatever the tool's own action
 * name is, so its params can be any arm of the action union. Read the single field positively
 * rather than asserting the line into a tool-result shape it may not have.
 */
const resultToolCallId = (line: ChatMessageLine): string | undefined => {
    if (!lineIsAction(line)) {
        return undefined;
    }
    const params: unknown = line.action_params;
    if (!params || typeof params !== "object" || !("tool_call_id" in params)) {
        return undefined;
    }
    const { tool_call_id } = params as { tool_call_id?: unknown };
    return typeof tool_call_id === "string" ? tool_call_id : undefined;
};

/**
 * Shown while a WorkIQ tool runs and it hasn't sent a status of its own yet. WorkIQ streams
 * its own lifecycle text for most calls (see `_stream_status_text` in vpoc/apps/workiq/integration.py),
 * so these are the opening frame rather than the whole story. Resolved when the label is computed,
 * not at module load, so it follows the current UI language.
 */
const WORKIQ_RUNNING_LABELS: Record<string, () => string> = {
    [ACTION.WORKIQ_CONTEXT_QUERY]: () => i18n.global.t("chat.workIq.running.contextQuery"),
    [ACTION.WORKIQ_UPCOMING_CALENDAR]: () => i18n.global.t("chat.workIq.running.upcomingCalendar"),
    [ACTION.WORKIQ_RECENT_MAIL]: () => i18n.global.t("chat.workIq.running.recentMail"),
    [ACTION.WORKIQ_UNREAD_MAIL]: () => i18n.global.t("chat.workIq.running.unreadMail"),
    [ACTION.WORKIQ_PREPARE_FOR_INTERACTION]: () => i18n.global.t("chat.workIq.running.prepareForInteraction"),
};

const runningLabel = (toolName: string) => WORKIQ_RUNNING_LABELS[toolName]?.() ?? i18n.global.t("chat.workIq.running.fallback");

const EMPHASISED_HEADLINE = /^\s*(?:\*\*|__)(.+?)(?:\*\*|__)/;
const MARKDOWN_HEADING = /^\s*#{1,6}\s+(.+)$/;
// An unmarked status that opens with a short sentence and then explains itself: keep the opener.
const SHORT_OPENER = /^(.{1,80}?[.!?])\s+\S/;

/*
 * The indicator is one line, but WorkIQ statuses arrive as markdown with a headline and a
 * paragraph of reasoning behind it:
 *
 *   **Searching relevant items** I am focusing on searching through various sources, including
 *   meetings, transcripts, chats, emails, and files, using targeted queries...
 *
 * Only the headline belongs on the line. Trailing punctuation goes too, because the indicator
 * renders its own ellipsis and a status that already ends in "..." would read as "......".
 */
const tidy = (text: string) =>
    text
        .replace(/[*`]/g, "")
        .replace(/^#+\s*/, "")
        .trim()
        .replace(/[.…:;,]+$/, "")
        .trim();

const headline = (text: string): string => {
    const firstLine = text.trim().split(/\r?\n/, 1)[0] ?? "";

    const emphasised = EMPHASISED_HEADLINE.exec(firstLine);
    if (emphasised) {
        return tidy(emphasised[1]);
    }

    const heading = MARKDOWN_HEADING.exec(firstLine);
    if (heading) {
        return tidy(heading[1]);
    }

    const opener = SHORT_OPENER.exec(firstLine);
    return tidy(opener ? opener[1] : firstLine);
};

const progressLabel = (toolName: string, statusText: string | undefined) => (statusText ? headline(statusText) : "") || runningLabel(toolName);

/*
 * `fromWorkIq` separates a step WorkIQ ran from one of Nadia's own, which is the whole of what
 * the line needs to know: hers carry the dot animation, WorkIQ's carry the source chip instead.
 */
export type WorkIqProgress = { label: string; fromWorkIq: boolean };

const thinkingFrame = (): WorkIqProgress => ({ label: i18n.global.t("chat.workIq.thinking"), fromWorkIq: false });

/*
 * What a single line says when the stack is read newest-first: progress to show, STOP because
 * what we found means there is nothing to show, or CONTINUE because this line settles nothing.
 */
const CONTINUE = "continue";
const STOP = "stop";
type LineVerdict = WorkIqProgress | typeof CONTINUE | typeof STOP;

const isProgress = (verdict: LineVerdict): verdict is WorkIqProgress => verdict !== CONTINUE && verdict !== STOP;

type ProgressInputs = { statusText: Map<string, string>; resolvedToolCallIds: Set<string>; workIqUser: boolean };

const collectResolvedToolCallIds = (messages: ChatMessage[]): Set<string> => {
    const resolved = new Set<string>();
    for (const message of messages) {
        if (message.role !== "tool") {
            continue;
        }
        for (const line of message.lines) {
            const toolCallId = resultToolCallId(line);
            if (toolCallId) {
                resolved.add(toolCallId);
            }
        }
    }
    return resolved;
};

const toolCallLinesOf = (message: ChatMessage) => message.lines.filter((line) => lineIsAction(line) && line.action_name === ACTION.TOOL_CALL);

/*
 * A completion's tool calls are all persisted on one message and then executed in order (see
 * `_handle_tool_calls`), so the step in progress is the first one still missing a result, not the
 * last one listed. Naming the last one would caption the wrong step and ignore the live status
 * text arriving for the running one. Once every call has returned, Nadia is working with the
 * results, so the final label holds until her answer arrives.
 */
const verdictForToolCalls = (lines: ChatMessageLine[], { statusText, resolvedToolCallIds }: ProgressInputs): LineVerdict => {
    const running = lines.find((line) => {
        const { tool_call_id } = toolCallParams(line);
        return !tool_call_id || !resolvedToolCallIds.has(tool_call_id);
    });

    // A non-WorkIQ tool owns the progress display through its own pill.
    const { tool_call_id, tool_name } = toolCallParams(running ?? lines[lines.length - 1]);
    if (!isWorkIqToolName(tool_name)) {
        return STOP;
    }

    return {
        label: progressLabel(tool_name, tool_call_id ? statusText.get(tool_call_id) : undefined),
        fromWorkIq: true,
    };
};

const verdictForLine = (line: ChatMessageLine, { workIqUser }: ProgressInputs): LineVerdict => {
    // Visible assistant prose means the reply has started; the user's own text means nothing
    // after it had progress to report.
    if (line.type === "md") {
        return !line.hidden && line.content.trim() ? STOP : CONTINUE;
    }

    if (!lineIsAction(line)) {
        return CONTINUE;
    }

    // The memory lookup hangs off the user message that triggered it, so it is the first thing
    // Nadia does in a turn and the natural first frame. It stays up once loaded, until a tool
    // call or the answer replaces it, and it is her own step rather than WorkIQ's.
    if (line.action_name === ACTION.LOADING) {
        const { content } = memoryLoadingParams(line);
        return workIqUser && content ? { label: headline(content), fromWorkIq: false } : CONTINUE;
    }

    return CONTINUE;
};

const verdictForMessage = (message: ChatMessage, inputs: ProgressInputs): LineVerdict => {
    const toolCalls = toolCallLinesOf(message);
    if (toolCalls.length > 0) {
        return verdictForToolCalls(toolCalls, inputs);
    }

    for (let i = message.lines.length - 1; i >= 0; i--) {
        const verdict = verdictForLine(message.lines[i], inputs);
        if (verdict !== CONTINUE) {
            return verdict;
        }
    }
    return CONTINUE;
};

/**
 * The single live progress line for a WorkIQ turn, or null when there is nothing to show.
 *
 * WorkIQ turns fire several tool calls back to back, and a status pill per call left a row of
 * stale pills parked in the stream forever. Instead we show one line that follows whatever
 * Nadia is doing right now — the memory lookup, then each WorkIQ call and its live status —
 * and drop it the moment her answer starts streaming (PLT-2308).
 *
 * The message stack is walked backwards from the newest line, so the first line that settles
 * anything decides the whole answer (see `verdictForLine`).
 *
 * `getIsWorkIqUser` follows the same prop that suppresses the memory pill, so the two can't
 * disagree about who renders "Checking my notes...". It also gates the opening frame, which is
 * what keeps this off the generic "Waiting for Nadia..." state for everyone else. The prop comes
 * from `has_workiq_connected`, which is `workiq_available_for_user`: the WORKIQ_INTEGRATION flag
 * plus per-org enablement plus live credentials.
 */
export function useWorkIqToolProgress(
    getMessages: () => ChatMessage[],
    getToolCallStatusText: () => Map<string, string>,
    getIsTurnInFlight: () => boolean,
    getIsWorkIqUser: () => boolean,
): { workIqProgress: ComputedRef<WorkIqProgress | null> } {
    const workIqProgress = computed<WorkIqProgress | null>(() => {
        // A finished turn has no progress to report. This is also what keeps an interrupted turn
        // quiet: reloading mid-tool-call orphans the turn, so its tool call stays unresolved
        // forever, and keying off the persisted call alone would spin a status that never lands.
        if (!getIsTurnInFlight()) {
            return null;
        }

        const messages = getMessages();
        const inputs: ProgressInputs = {
            statusText: getToolCallStatusText(),
            resolvedToolCallIds: collectResolvedToolCallIds(messages),
            workIqUser: getIsWorkIqUser(),
        };

        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];

            // Tool-result messages carry no progress of their own; the assistant tool call that
            // asked for them does.
            if (message.role !== "assistant" && message.role !== "user") {
                continue;
            }

            const verdict = verdictForMessage(message, inputs);
            if (isProgress(verdict)) {
                return verdict;
            }

            // A visible user message is the turn boundary: nothing after it reported a step, and
            // the user's own text is not progress, so the opening frame belongs here. This is
            // checked ahead of STOP for exactly that reason — their prose is not a stop signal.
            // Work IQ users only; for everyone else the generic waiting states own this gap.
            if (message.role === "user" && !message.hidden) {
                return inputs.workIqUser ? thinkingFrame() : null;
            }

            if (verdict === STOP) {
                return null;
            }
        }

        return null;
    });

    return { workIqProgress };
}

/**
 * Hidden user messages are sent by the app on the user's behalf mid-turn, so only a visible one
 * starts a new turn; an unresolved call from before it belongs to an earlier, abandoned turn.
 */
export function inFlightToolName(messages: ChatMessage[]): string | null {
    const resolved = collectResolvedToolCallIds(messages);
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.role === "user" && !message.hidden) {
            return null;
        }
        for (const line of toolCallLinesOf(message)) {
            const { tool_call_id, tool_name } = toolCallParams(line);
            if (tool_name && !(tool_call_id && resolved.has(tool_call_id))) {
                return tool_name;
            }
        }
    }
    return null;
}

/** Every call to `toolName` since the latest visible user message, resolved or not. */
export function currentTurnToolCallIds(messages: ChatMessage[], toolName: string): Set<string> {
    const ids = new Set<string>();
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.role === "user" && !message.hidden) {
            break;
        }
        for (const line of toolCallLinesOf(message)) {
            const { tool_call_id, tool_name } = toolCallParams(line);
            if (tool_name === toolName && tool_call_id) {
                ids.add(tool_call_id);
            }
        }
    }
    return ids;
}

/** Every call to `toolName` in the current turn still waiting on a result, including ones queued behind another tool. */
export function pendingToolCallIds(messages: ChatMessage[], toolName: string): Set<string> {
    const resolved = collectResolvedToolCallIds(messages);
    return new Set([...currentTurnToolCallIds(messages, toolName)].filter((id) => !resolved.has(id)));
}
